import { useFieldArray, useFormContext } from 'react-hook-form';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { IconButton } from '@/app/components/buttons/IconButton';
import { Input } from '@/app/components/form/Input';
import type { CreateClientFormData } from '@/types/clientsApiTypes';
import {
  additionalEmailAtPath,
  additionalEmailsPath,
  primaryEmailPath,
  type ClientEmailsFieldPrefix,
} from './types-util';
import DangerIcon from '/public/icons/delete.svg';
import PlusIcon from '/public/icons/plus.svg';

const MAX_EMAILS_COUNT = 2;

interface ClientEmailsProps<T extends CreateClientFormData = CreateClientFormData> {
  onUpdateFormData?: (fieldName: string, fieldValue: unknown) => void;
  fieldPrefix: ClientEmailsFieldPrefix<T>;
  isSmallDeleteButton?: boolean;
}

export function ClientEmails<T extends CreateClientFormData = CreateClientFormData>({
  onUpdateFormData,
  fieldPrefix,
  isSmallDeleteButton,
}: ClientEmailsProps<T>) {
  const {
    watch,
    formState: { errors },
    register,
  } = useFormContext<T>();
  const primaryEmail = watch(primaryEmailPath(fieldPrefix));

  const isPrimaryEmailValid =
    !!primaryEmail &&
    !(String(fieldPrefix) === 'primary_passenger.client'
      ? (errors as any)?.primary_passenger?.client?.email
      : undefined);

  const { fields, append, remove } = useFieldArray({
    name: additionalEmailsPath(fieldPrefix),
  });

  const canAddEmail = isPrimaryEmailValid && fields.length < MAX_EMAILS_COUNT;

  const onAddEmail = () => {
    if (fields.length < MAX_EMAILS_COUNT) {
      append({ email: '' } as any);
    }
  };

  const onDeleteEmail = (index: number) => {
    remove(index);
    onUpdateFormData?.(
      `${fieldPrefix}.additional_emails`,
      fields.filter((_, i) => i !== index)
    );
  };

  const handleSaveOnBlur = (name: string, value: unknown) => {
    onUpdateFormData?.(name, value);
  };

  return (
    <div className="flex flex-col gap-[22px] w-full max-md:gap-4">
      <Input
        label="Primary email"
        required
        placeholder="Enter email"
        className="w-full"
        {...register(primaryEmailPath(fieldPrefix))}
        errors={errors}
        onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
      />

      {fields.map((field, index) => (
        <div key={field.id} className="flex gap-2 items-start w-full">
          <div className="flex-1 w-full min-w-0 relative">
            <Input
              className="w-full"
              label="Additional email"
              placeholder="Enter email"
              {...register(additionalEmailAtPath(fieldPrefix, index))}
              errors={errors}
              onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
            />
            {isSmallDeleteButton ? (
              <button onClick={() => onDeleteEmail(index)} className="cursor-pointer block">
                <DangerIcon className="absolute top-0 right-0 w-4 h-4 text-red-500" />
              </button>
            ) : null}
          </div>

          {isSmallDeleteButton ? null : (
            <div className="mt-7 shrink-0">
              <IconButton
                color="danger"
                size="xl"
                onClick={() => onDeleteEmail(index)}
                icon={<DangerIcon className="w-6 h-6 text-red-500" />}
              />
            </div>
          )}
        </div>
      ))}

      <BaseButton
        intent="inline"
        startIcon={<PlusIcon className="w-4 h-4" />}
        disabled={!canAddEmail}
        type="button"
        onClick={onAddEmail}
        className="text-[16px]"
      >
        Add Another Email
      </BaseButton>
    </div>
  );
}